/**
 * QR Code Decoder Tool
 */

import jsQR from 'jsqr';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

function extractDataUrl(input: string): string {
  const trimmed = String(input ?? '').trim();
  if (trimmed.startsWith('data:image/')) return trimmed;
  // If it's raw base64, assume PNG
  if (/^[A-Za-z0-9+/\s]+=*$/.test(trimmed)) {
    const base64 = trimmed.replace(/\s+/g, '');
    return `data:image/png;base64,${base64}`;
  }
  throw ToolError.validation('Input must be an image data URL or base64 image data');
}

async function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image')); 
    img.src = dataUrl;
  });
}

export const QrCodeDecoderTool: ITransformerTool = {
  id: 'qr-code-decoder',
  name: 'QR Code Decoder',
  description: 'Decode a QR code from an image data URL',
  version: '1.0.0',
  type: 'transformer',
  category: 'Generators',
  keywords: ['qr', 'qrcode', 'decode', 'scanner', 'image'],

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [],

  async transform(input: string): Promise<string> {
    // This tool requires DOM APIs.
    if (typeof document === 'undefined') {
      throw ToolError.execution('QR decode requires a browser environment');
    }

    const dataUrl = extractDataUrl(input);

    try {
      const img = await loadImage(dataUrl);
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) throw ToolError.execution('Canvas not available');
      ctx.drawImage(img, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height);
      if (!code?.data) throw ToolError.validation('No QR code found in image');
      return code.data;
    } catch (e) {
      if (e instanceof ToolError) throw e;
      const msg = e instanceof Error ? e.message : 'Failed to decode QR code';
      if (msg.includes('No QR')) throw ToolError.validation(msg);
      throw ToolError.execution(msg);
    }
  },
};
